import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Eye,
  Calendar,
  User,
  Search,
  Plus,
  FileText,
  Clock,
  ArrowRight,
  Camera,
  TrendingUp,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { PageContainer } from "@/components/pro/PageContainer";
import { PageHeader } from "@/components/pro/PageHeader";

interface IridologySession {
  id: string;
  session_date: string;
  consultation_type: string;
  eye_observed: string;
  vitality_level: string | null;
  created_at: string;
  patient_id: string;
  patient?: {
    name: string;
  };
  iris_photos?: {
    id: string;
    eye_side: string;
    photo_url: string;
  }[];
}

const IridologyDashboard = () => {
  const [sessions, setSessions] = useState<IridologySession[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [stats, setStats] = useState({
    totalSessions: 0,
    thisMonth: 0,
    photosCount: 0,
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("iridology_sessions")
        .select(`
          *,
          patient:patients(name),
          iris_photos(id, eye_side, photo_url)
        `)
        .eq("user_id", user.id)
        .order("session_date", { ascending: false });

      if (error) throw error;

      setSessions(data || []);

      // Calculate stats
      const now = new Date();
      const thisMonth = data?.filter(s => {
        const sessionDate = new Date(s.session_date);
        return sessionDate.getMonth() === now.getMonth() && 
               sessionDate.getFullYear() === now.getFullYear();
      }).length || 0;

      const photosCount = data?.reduce((acc, s) => acc + (s.iris_photos?.length || 0), 0) || 0;

      setStats({
        totalSessions: data?.length || 0,
        thisMonth,
        photosCount,
      });
    } catch (error: any) {
      console.error("Erreur chargement séances:", error);
      toast.error("Erreur lors du chargement des séances");
    } finally {
      setLoading(false);
    }
  };

  const filteredSessions = sessions.filter(session => {
    const patientName = session.patient?.name || "";
    return patientName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getConsultationTypeLabel = (type: string) => {
    switch (type) {
      case "iridology_only":
        return "Iridologie seule";
      case "iridology_naturopathy":
        return "Iridologie + Naturopathie";
      default:
        return type;
    }
  };

  const getEyeLabel = (eye: string) => {
    switch (eye) {
      case "right":
        return "Œil droit";
      case "left":
        return "Œil gauche";
      case "both":
        return "Les deux yeux";
      default:
        return eye;
    }
  };

  const getVitalityBadge = (level: string | null) => {
    switch (level) {
      case "good":
        return <Badge className="bg-green-500/20 text-green-700 border-green-500/30">Bonne</Badge>;
      case "average":
        return <Badge className="bg-yellow-500/20 text-yellow-700 border-yellow-500/30">Moyenne</Badge>;
      case "needs_support":
        return <Badge className="bg-orange-500/20 text-orange-700 border-orange-500/30">À soutenir</Badge>;
      default:
        return null;
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Iridologie"
        subtitle="L'iridologie est un outil d'observation du terrain permettant de mieux comprendre les forces, sensibilités et besoins globaux du consultant."
        icon={<Eye className="w-6 h-6 sm:w-7 sm:h-7" />}
        iconColor="primary"
        actions={
          <Link to="/iridology/new">
            <Button className="gap-2 bg-gradient-primary shadow-md">
              <Plus className="w-4 h-4" />
              Nouvelle séance
            </Button>
          </Link>
        }
      />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-primary">{stats.totalSessions}</p>
                <p className="text-sm text-muted-foreground">Séances totales</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-secondary/50 border-secondary">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center">
                <Calendar className="w-6 h-6 text-secondary-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.thisMonth}</p>
                <p className="text-sm text-muted-foreground">Ce mois-ci</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-accent/50 border-accent">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center">
                <Camera className="w-6 h-6 text-accent-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.photosCount}</p>
                <p className="text-sm text-muted-foreground">Photos d'iris</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info Card */}
        <Card className="mb-8 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">Cette section vous permet :</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• De prendre ou importer des photos de l'iris</li>
                  <li>• De structurer vos observations</li>
                  <li>• De relier l'analyse aux recommandations naturopathiques</li>
                  <li>• De générer des comptes-rendus professionnels</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par nom de consultant..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Sessions List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <Skeleton className="w-12 h-12 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-1/3" />
                      <Skeleton className="h-3 w-1/4" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredSessions.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Eye className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Aucune séance d'iridologie</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm 
                  ? "Aucun résultat pour cette recherche" 
                  : "Commencez par créer votre première séance d'iridologie"}
              </p>
              {!searchTerm && (
                <Link to="/iridology/new">
                  <Button className="gap-2">
                    <Plus className="w-4 h-4" />
                    Nouvelle séance
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredSessions.map((session) => (
              <Card 
                key={session.id} 
                className="hover:shadow-md transition-all cursor-pointer group"
                onClick={() => navigate(`/iridology/${session.id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Photos preview */}
                    <div className="flex-shrink-0">
                      {session.iris_photos && session.iris_photos.length > 0 ? (
                        <div className="relative w-16 h-16 rounded-full overflow-hidden border-2 border-primary/20">
                          <img 
                            src={session.iris_photos[0].photo_url} 
                            alt="Iris" 
                            className="w-full h-full object-cover"
                          />
                          {session.iris_photos.length > 1 && (
                            <div className="absolute bottom-0 right-0 w-6 h-6 bg-primary text-primary-foreground rounded-full text-xs flex items-center justify-center font-bold">
                              +{session.iris_photos.length - 1}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                          <Eye className="w-8 h-8 text-muted-foreground/50" />
                        </div>
                      )}
                    </div>

                    {/* Session info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="font-semibold truncate">
                          {session.patient?.name || "Consultant inconnu"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                        <Clock className="w-3 h-3" />
                        <span>{format(parseISO(session.session_date), "d MMMM yyyy", { locale: fr })}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline" className="text-xs">
                          {getConsultationTypeLabel(session.consultation_type)}
                        </Badge>
                        <Badge variant="secondary" className="text-xs">
                          {getEyeLabel(session.eye_observed)}
                        </Badge>
                        {getVitalityBadge(session.vitality_level)}
                      </div>
                    </div>

                    {/* Arrow */}
                    <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
    </PageContainer>
  );
};

export default IridologyDashboard;
